import { type ReactNode } from "react";
import { Box, Link, Paper } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ChecklistRow, { AskText, SectionHeading } from "./ChecklistRow";
import { SECTION_ROWS } from "./sectionRows";
import { textLinkSx } from "../common/textLinkStyles";
import { storefrontSetupHref } from "../../utils/storefrontLinks";
import { DocumentSection, EVIDENCE_ATTENTION_LABELS, EvidenceAttentionCode, EvidenceAttentionItem } from "../../types/evidence";

const itemKey = (item: EvidenceAttentionItem) =>
  `${item.code}-${item.attachableSlot ?? ""}-${item.message}`;

// Prod holds drafts stamped with codes this build no longer knows, and they keep their codes until
// they are regenerated; a row headed by nothing reads as a bug.
const attentionLabel = (code: string): string => {
  const labels: Partial<Record<string, string>> = EVIDENCE_ATTENTION_LABELS;
  return labels[code] ?? `${code.charAt(0).toUpperCase()}${code.slice(1).replace(/_/g, " ")}`;
};


// Findings no section row owns: an item with a slot sits on its row in the Sections card, and the
// conclusion's own row already reports a missing conclusion. The storefront gaps share one header
// action: an order resolves to one storefront, and every gap of it is closed on the same settings page.
export default function GoodToKnow({ items, rowKeys, settingsLink, help }: {
  items: EvidenceAttentionItem[];
  /** The Sections card's rows: a note a listed row carries as its status line is not repeated here. */
  rowKeys: DocumentSection[];
  /** The user can open the company page the storefront settings live on. */
  settingsLink: boolean;
  help?: ReactNode;
}) {
  const rowNotes: EvidenceAttentionCode[] = rowKeys.flatMap((key) => SECTION_ROWS[key].notes ?? []);
  const notes = items.filter((item) => !item.attachableSlot && !rowNotes.includes(item.code) && item.code !== "conclusion_missing");
  if (notes.length === 0) return null;
  const storefrontSetup = settingsLink ? notes.find((item) => item.storefrontSetup)?.storefrontSetup : undefined;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <SectionHeading action={(
        <>
          {help}
          {storefrontSetup && (
            <Link
              href={storefrontSetupHref(storefrontSetup)}
              target="_blank"
              rel="noopener"
              underline="hover"
              sx={{ ...textLinkSx, ml: "auto", display: "inline-flex", alignItems: "center", gap: 0.5 }}
            >
              Storefront settings
              <OpenInNewIcon sx={{ fontSize: 14 }} />
            </Link>
          )}
        </>
      )}>
        Findings
      </SectionHeading>
      <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
        {notes.map((item) => (
          <ChecklistRow
            key={itemKey(item)}
            // A red title needs a decision; a recommended finding is context, so its title stays plain.
            primary={<AskText severity={item.severity === "required" ? "required" : null}>{attentionLabel(item.code)}</AskText>}
            secondary={item.message}
          />
        ))}
      </Box>
    </Paper>
  );
}
